import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment'
import { LogService } from './log.service';
import { TranslationService } from './translation.service';
import { LocalizationService } from './localization.service';
import { catchError, map, share } from 'rxjs/operators';
import { BehaviorSubject, throwError, Observable, Subject, of, empty } from 'rxjs';
import { SimulatorConfig, NavBarLink, LanguageDefinition, MenuNode } from '../classes';
import { FileCacheService } from './file-cache.service';
@Injectable({ providedIn: 'root' })

export class ConfigService {

    _debug:boolean = false;
    private debugLevel = 0;

    private debug( msg : string ) : void {
        if(this._debug){
            console.log("ConfigService: "+msg);
        }
    }
    baseURL = '';
    downloadedFiles = new Array<string>();
    config$=new BehaviorSubject<SimulatorConfig>(new SimulatorConfig({}));
    menu : MenuNode;
    menu$ = new BehaviorSubject<MenuNode>(new MenuNode());
    languageDefinitions:Array<LanguageDefinition>;
    availableLanguages$ = new BehaviorSubject<Array<LanguageDefinition>>(new Array<LanguageDefinition>());
    menuRawData:any;
    currentLanguage:string;
    _complete = false;
    _preload = true;
    _footerLinks = new Array<NavBarLink>();
    language : LanguageDefinition;
    defaultLanguage : LanguageDefinition;    
    events$=new BehaviorSubject<string>("null");
    version = 1.09;
    _simulatorConfig:SimulatorConfig;
    get simulatorConfig():SimulatorConfig{
        return this._simulatorConfig;        
    }
    
    set simulatorConfig(value:SimulatorConfig){
        if(!this._simulatorConfig){
            this._simulatorConfig = value;
        }else{
            this._simulatorConfig.construct(value);
        }
    }
    enablePreloading(){
        this._preload = true;
    }
    disablePreloading(){
        this._preload = false;
    }
    preloadingEnabled(){
        return this._preload;
    }
    constructor(
        private log: LogService, 
        private localization: LocalizationService, 
        private translationService: TranslationService,
        private fileCache: FileCacheService) {
        this.defaultLanguage = new LanguageDefinition({
            "countryCode":"en",
            "countryNameNative":"default"
        });
        this.language = this.defaultLanguage;
        this.debug("constructor called.");
        this.debug("Loading translation-index.");
        this.loadTranslationIndex()
        .then(
            (loadTranslationIndexData)=>{
                this.debug("Loading UI-Texts");
                this.getUITexts()
                .then(
                    (uiTextsData)=>{
                        this.debug("Loading browser-language.");
                        this.loadLanguage(this.getBrowserLanguage())
                        .then(
                            (languageData)=>{
                                this.debug("setting language to browser-language.");
                                this.setLanguage(this.getBrowserLanguage())
                                this.debug("Loading config.");
                                this.loadConfig()
                                .then(
                                    (data)=>{
                                        this.debug("Loading menu.");
                                        this.loadMenu()
                                        .then(
                                            (menuData)=>{
                                                this.debug("loading config.");
                                                this.events$.next('all_files_loaded');
                                            }
                                        );
                                    }
                                );
                            }
                        );
                    }
                );
            }
        );



        this.simulatorConfig = new SimulatorConfig({});
    }

    // reads ui-texts from json-file, sets it for every language.
    
    private getUITexts() : Promise<any> {
        
        this.debug("Loading ui-texts");
    
        return this.fileCache.getFile(this.baseURL+'/assets/languages/config.translations.json?v='+this.version).then(
            (data)=>{
                
                if(data === undefined || data === null){
                    this.debug("data is empty or undefined in getUITexts() after reading "+this.baseURL+'/assets/languages/config.translations.json?v='+this.version);
                    return;
                }
            
                for(let ld of this.getLanguageDefinitions()){
                    //this.debug("Adding "+ld.getCountryCode());
                    this.translationService.addToDictionary(data,ld.getCountryCode());

                    this.events$.next("uitexts_loaded");
                }

            },
            (err)=>{
                this.debug("Error fetching "+this.baseURL+'/assets/languages/config.translations.json?v='+this.version);
            }
        );
    }
    // loads index-file of all currently supported languages, containing where where to find translations for each language
    loadTranslationIndex() : Promise<any>{
        this.debug("Loading translation index");
        return this.fileCache.getFile(this.baseURL+'/assets/languages/languages.json?v='+this.version).then(
            (data)=>{
                if(!data){
                    this.debug("got no data to work with in loadTranslationIndex.");
                    return;
                }
                this.languageDefinitions=new Array<LanguageDefinition>();

                for(let ld of data['languages']){
                    let langDef = new LanguageDefinition(ld);
                    //this.debug("Found language "+langDef.getCountryCode());
                    if(langDef.getCountryCode() == this.getBrowserLanguage()){
                        this.language = langDef;
                    }

                    this.languageDefinitions.push(langDef);
                    this.events$.next("translationindex_loaded");
                }
            },
            (error)=>{
                this.debug("error loading language-definitions.");
            }
        );
    }

    // loads translation-file as configured in current language-definitions file
    private async loadLanguage(lang:string) : Promise<any>{
        let langFile = this.baseURL+'/assets/languages/'+this.getLanguageDefinition(lang).getLanguageFile();
        console.log("Loading language-file: "+langFile);
        return this.fileCache.getFile(langFile+"?v="+this.version)
        .then(
            (data)=>{
                this.translationService.addToDictionary(data,lang,true);
                this.loadInfoText(lang);
                this.events$.next('language_loaded');
                this.events$.next('language_loaded_'+lang);
            }
        );
    }


    getLanguageDefinitions(){
        return this.languageDefinitions;
    }
    get config(){
        return this.simulatorConfig;
    }
    isConfigured(){
        if(this.simulatorConfig){
            return this.simulatorConfig.isConfigured();
        }
        return false;
    }
    isKnownLanguage(lang:string):boolean{
        if(!this.languageDefinitions){
            return lang == this.defaultLanguage.getCountryCode();
        }
        for(let ld of this.languageDefinitions){
            if(ld.getCountryCode() == lang){
                return true;
            }
        }
        return false;
    }


    parseLanguageData(data,ld,use?:boolean,emit?:boolean){
        this.translationService.addToDictionary(data,ld.getCountryCode(),true);
        if(use){
            this.translationService.useLanguage(ld.getCountryCode());
        }
        if(emit){
            this.events$.next('languageLoaded');
        }
    }
    getLanguageDefinition(cc:string):LanguageDefinition{
        for(let ld of this.languageDefinitions){
            if(ld.getCountryCode() == cc){
                return ld;
            }
        }

        this.debug("Returning default-language, did not find "+cc);
        return this.defaultLanguage;
    }
    loadInfoText( lang : string ) : Promise<any> {
        return this.fileCache.getFile(this.baseURL+'/assets/languages/'+this.getLanguageDefinition(lang).getInfoTextFile()+"?v="+this.version)
        .then(
            (data)=>{
                this.translationService.setInfoTexts(data,this.getCountryCode());
            }
        );
    }
    async setLanguageBlocking(lang:string){
        let val = await this.setLanguage(lang);
        return val;
    }
    async setLanguage(lang:string){
        if(!this.isKnownLanguage(lang)){
            return this.setLanguage(this.defaultLanguage.getCountryCode());
        }
        if(this.translationService.hasLanguage(lang)){
            this.translationService.useLanguage(lang);
        }
        this.debug("Loading language: "+lang);
        return await this.loadLanguage(lang).then(
            (data)=>{
                let oldLang = this.getCurrentLanguage();
                this.translationService.addToDictionary(data,lang,true);
                this.translationService.useLanguage(lang);
                for(let ld of this.languageDefinitions){
                    if(ld.getCountryCode() == lang){
                        this.language = ld;
                    }
                }
                this.loadFooterLinks();
                if(oldLang != this.getCurrentLanguage()){
                    this.events$.next('language_changed');
                }
                
            }
        );
    }

    getCurrentLanguageDefinition() : LanguageDefinition{
        return this.language;
    }
    getAvailableLanguages(){
        let arr = [];
        for(let ld of this.languageDefinitions){
            arr.push(ld.getCountryNameNative());
        }
    }

    get footerLinks(){
        return this._footerLinks;
    }

    loadFooterLinks(){
        this.debug("Loading Footer Links from "+this.getCountryCode()+".config.json?v="+this.version);
        this.fileCache.getFile(this.baseURL+'assets/configurations/'+this.getCountryCode()+'.config.json?v='+this.version).then(
            (data)=>{
                if(data['footer'] && data['footer']['footerLinks']){
                    this._footerLinks.length = 0;
                    for(let flData of data['footer']['footerLinks']){
                        this._footerLinks.push(new NavBarLink(flData));
                    }
                }else{

                }
            }
        );
    }
    
    reloadMenu(){
        this.debug("Reloading menu from raw data.");
        this.menu = new MenuNode(JSON.parse(this.menuRawData));
        MenuNode.setRoot(this.menu);
        this.menu$.next(this.menu);
        if(!this._complete){
            this.events$.next('completed');
            this._complete = true;
        }
        this.events$.next('menu_loaded');
    }
    loadConfig(): Promise<any>{
        this.debug("Loading config for country-code: "+this.getCountryCode());
        return this.fileCache.getFile(this.baseURL+'/assets/configurations/'+this.getConfigPrefix()+'config.json?v='+this.version).then(
            (data)=>{
                if(!this.simulatorConfig){
                    this.simulatorConfig = new SimulatorConfig(data);
                }else{
                    this.simulatorConfig.construct(data);
                }
                this.debug("Got simulator-config: "+this.simulatorConfig.id);
                this.config$.next(this.simulatorConfig);
            },
            (error)=>{this.debug("error loading config.");}
            );

        
    }
    
    getCountryCode(){
        if(!this.getCurrentLanguageDefinition()){
            this.debug("returning default language");
            return this.defaultLanguage.getCountryCode();
        }
        this.debug("returning "+this.getCurrentLanguageDefinition().getCountryCode());
        return this.getCurrentLanguageDefinition().getCountryCode();
    }

    getBrowserLanguage(){
        var lang = window['navigator']['language'] || window['navigator']['userLanguage'];  
        if(this.isKnownLanguage(lang)){
            return lang;
        }

        switch(lang.toLowerCase()){
            case 'de-lu':
            case 'de-ch':
            case 'de-at':
            case 'de-de':{
                return 'de';
            }
            case 'ar-dz':
            case 'ar-bh':
            case 'ar-eg':
            case 'ar-iq':
            case 'ar-jo':
            case 'ar-kw':
            case 'ar-lb':
            case 'ar-ly':
            case 'ar-ma':
            case 'ar-om':
            case 'ar-qa':
            case 'ar-sa':
            case 'ar-sy':
            case 'ar-tn':
            case 'ar-ye':
            case 'ar-ae':
            case 'ar':{
                return 'ar';
            }
            case 'zh-cn':{
                return 'zh-CN';
            }
            case 'zh':{
                return 'zh-CHS';
            }
            case 'nl-be':{
                return 'nl';
            }
            case 'fr-be':
            case 'fr-ca':
            case 'fr-fr':
            case 'fr-lu':
            case 'fr-ch':
            case 'fr-mo':{
                return 'fr';
            }
            case 'ko-kr':
            case 'ko-kp':
            case 'ko':{
                return 'ko';
            }
            case 'it-ch':{
                return 'it';
            }
            case 'nb':
            case 'nn':{
                return 'no';
            }
            case 'pt-br':{
                return 'pt';
            }
            case 'ru-mo':
            case 'ru':{
                return 'ru';
            }
            case 'sr':{
                return 'sr-SP';
            }
            case 'es-ar':
            case 'es-bo':
            case 'es-cl':
            case 'es-co':
            case 'es-cr':
            case 'es-do':
            case 'es-ec':
            case 'es-sv':
            case 'es-gt':
            case 'es-hn':
            case 'es-mx':
            case 'es-ni':
            case 'es-pa':
            case 'es-py':
            case 'es-pe':
            case 'es-pr':
            case 'es-es':
            case 'es-uy':
            case 'es-ve':{
                return 'es';
            }
            case 'sv-fi':
            case 'sv-sv':
            case 'sv':{
                return 'se';
            }
            case 'uk':{
                return 'ua';
            }

            default:{
                return 'en';
            }
        }
        return this.defaultLanguage.getCountryCode();
    }
    getCurrentLanguage(){
        return this.getCurrentLanguageDefinition().getCountryCode();
    }
    
    async loadTranslations(){
        
        for(let ld of this.languageDefinitions){
            if(ld.getCountryCode() == this.getCountryCode()){
                this.fileCache.getFile(this.baseURL+'/assets/languages/'+ld.getLanguageFile()+'?v='+this.version).then(
                    (data)=>{
                        this.parseLanguageData(data,ld,false,false);
                        MenuNode.setTranslator(this.translationService);
                        this.loadMenu();
                    },
                    (error)=>{this.debug("Error loading translation");}
                    );
                this.fileCache.getFile(this.baseURL+'/assets/languages/'+ld.getInfoTextFile()+'?v='+this.version).then(
                    (data)=>{
                        this.translationService.setInfoTexts(data,this.getCountryCode());
                    },
                    (error)=>{this.debug("Error loading info-Texts");}
                    );
            }
        }
    }


    loadMenu():Promise<any>{
        return this.fileCache.getFile(this.baseURL+'/assets/configurations/'+this.simulatorConfig.getMenuFilename()+"?v="+this.version).then(
            (data)=>{
                this.debug("Loading menu.");
                this.menuRawData = JSON.stringify(data);
                this.reloadMenu();
                if(this._preload){
                    for(let ld of this.languageDefinitions){
                        if(!this.fileCache.has(this.baseURL+'/assets/languages/'+ld.getLanguageFile() +'?v='+this.version) ){
                            this.fileCache.getFile( this.baseURL+'/assets/languages/'+ld.getLanguageFile()+'?v='+this.version ).then((data)=>{
                                this.translationService.addToDictionary(data,ld.getCountryCode(),true);
                            });
                        }
                        if(!this.fileCache.has( this.baseURL+'/assets/languages/'+ld.getInfoTextFile()+'?v='+this.version) ){
                            this.fileCache.getFile(this.baseURL+'/assets/languages/'+ld.getInfoTextFile()+'?v='+this.version).then((data)=>{
                                this.translationService.setInfoTexts(data,ld.getCountryCode());
                            });

                        }
                    }
                }

            },
            (error)=>{this.debug("error loading menu.");}
        );
    }
    complete() : boolean{
        return this._complete;
    }
    _uiLanguage :string;
    setUiLanguage(lang:string){
        this._uiLanguage = lang;
    }
    getUiLanguage():string{
        return this._uiLanguage;
    }
    getConfigPrefix():string{
        
        let path = window.location.pathname.split('/');
        let country = '';
        for(let i=0;i<path.length;i++){
            if(path[i] != ""){
                country = path[i];
            }
        }
        let configPrefix = 'de';
        switch(country){
            case 'de':
            case 'fr':
            case 'it':
            case 'nl':
            case 'es':
            case 'tr':
            case 'hu':
            case 'ru':
            case 'ua':
            case 'lv':
            case 'lt':
            case 'se':
            case 'no':
            case 'pl':
            case 'hr':
            case 'ro':
            case 'pt':
            case 'bg':
            case 'be':
            case 'at':
            case 'ch':
            case 'sk':
            case 'fi':
            case 'mk':
            case 'kr':{
                configPrefix = country;
                break;
            }
            case 'gr':{
                configPrefix = 'el';
                break;
            }
            case 'al':{
                configPrefix = 'sq';
                break;
            }
            case 'rs':{
                configPrefix = 'sr-SP';
                break;
            }
            case 'si':{
                configPrefix = 'sl';
                break;
            }
            case 'cz':{
                configPrefix = 'cs';
                break;
            }
            case 'gb':
            case 'en':{
                configPrefix = 'en';
                break;
            }
            case 'ee':{
                configPrefix = 'et';
                break;
            }
            case 'dk':{
                configPrefix = 'da';
                break;
            }
            case 'ec':{
                configPrefix = 'ec';
                break;
            }
            case 'cn':{
                configPrefix = 'zh-CN';
                break;
            }
            default:{
                configPrefix = '';
                return configPrefix;
            }
        }
        return configPrefix+".";
    }
}
